import { getTrainabilityScore10 } from "./TrainabilityUtils";
import { normalizePlayerPosition } from "./PlayerPositions";

const DEFAULT_TRAINABILITY_THRESHOLD = 4;

export const PLAYER_LOAD_SIMULATION_METRIC_KEYS = [
  "trainability_index",
  "mechanical_fatigue",
  "intensity_index",
  "RII",
  "mechanical_load_index",
  "MLI",
  "time_on_pitch_min",
  "global_distance",
  "hsr_distance_m",
  "spr_distance_m",
  "sprint_max",
  "sprint_count",
  "COD_number",
  "acceleration_max",
  "deceleration_max",
  "accel_count",
  "decel_count",
];

const INACTIVE_ALERT_STATUSES = new Set([
  "resolved",
  "closed",
  "inactive",
  "archived",
  "deleted",
  "dismissed",
  "cleared",
]);

function toPositiveNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function normalizePlayerId(value) {
  if (value === null || value === undefined) return null;
  const asString = String(value).trim();
  return asString.length > 0 ? asString : null;
}

function isTruthyFlag(value) {
  if (value === true) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
}

function isInactiveStatus(status) {
  if (status === null || status === undefined) return false;
  const normalized = String(status).trim().toLowerCase();
  return INACTIVE_ALERT_STATUSES.has(normalized);
}

function hasNumericValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "N/A") return false;
  }
  return Number.isFinite(Number(value));
}

export function hasLoadSimulationData(player) {
  if (!player || typeof player !== "object") return false;
  if (normalizePlayerPosition(player.position) === "Goalkeeper") return false;

  const hasSimulationDate =
    typeof player.timeinfo_date === "string" &&
    player.timeinfo_date.trim() !== "" &&
    player.timeinfo_date !== "N/A";

  const hasLoadMetric = PLAYER_LOAD_SIMULATION_METRIC_KEYS.some((key) =>
    hasNumericValue(player[key])
  );

  return hasSimulationDate || hasLoadMetric;
}

export function getAlertPlayerId(alert) {
  if (!alert || typeof alert !== "object") return null;
  return normalizePlayerId(alert.player_id ?? alert.player?.id ?? alert.payload?.player_id);
}

export function buildActiveAlertPlayerIdSet(alerts) {
  const ids = new Set();
  if (!Array.isArray(alerts)) return ids;

  for (const alert of alerts) {
    if (!alert || typeof alert !== "object") continue;
    if (alert.deleted_at) continue;
    if (isInactiveStatus(alert.status)) continue;

    const playerId = getAlertPlayerId(alert);
    if (playerId) ids.add(playerId);
  }

  return ids;
}

export function hasPlayerActiveAlert(player, options = {}) {
  if (!player || typeof player !== "object") return false;

  const playerId = normalizePlayerId(player.id ?? player.player_id);
  const activeAlertPlayerIds = options.activeAlertPlayerIds;

  if (playerId && activeAlertPlayerIds instanceof Set && activeAlertPlayerIds.has(playerId)) {
    return true;
  }

  if (playerId && activeAlertPlayerIds instanceof Map && activeAlertPlayerIds.get(playerId)) {
    return true;
  }

  if (playerId && Array.isArray(activeAlertPlayerIds)) {
    const normalizedIds = activeAlertPlayerIds.map((id) => normalizePlayerId(id));
    if (normalizedIds.includes(playerId)) return true;
  }

  if (typeof options.isAlertActive === "function") {
    try {
      if (options.isAlertActive(player)) return true;
    } catch {
      // Ignore invalid custom predicates and continue with built-in checks.
    }
  }

  const booleanFields = [
    "has_active_alert",
    "active_alert",
    "alert_active",
    "is_alert_active",
    "hasAlert",
  ];
  for (const key of booleanFields) {
    if (isTruthyFlag(player[key])) return true;
  }

  const countFields = [
    "active_alert_count",
    "active_alerts_count",
    "alerts_count",
    "unread_alert_count",
    "unread_alerts_count",
  ];
  for (const key of countFields) {
    if (toPositiveNumber(player[key]) > 0) return true;
  }

  const listCandidates = [player.active_alerts, player.alerts, player.player_alerts];
  for (const list of listCandidates) {
    if (Array.isArray(list) && list.length > 0) return true;
    if (list && typeof list === "object" && !Array.isArray(list) && Object.keys(list).length > 0) {
      return true;
    }
  }

  const statusCandidates = [player.alert_status, player.risk_alert_status];
  for (const status of statusCandidates) {
    if (!status) continue;
    const normalized = String(status).trim().toLowerCase();
    if (!INACTIVE_ALERT_STATUSES.has(normalized) && normalized !== "none" && normalized !== "no_alert") {
      return true;
    }
  }

  return false;
}

export function isPlayerAtRisk(player, options = {}) {
  if (!player || typeof player !== "object") return false;

  const thresholdValue = Number(options.trainabilityThreshold);
  const trainabilityThreshold = Number.isFinite(thresholdValue)
    ? thresholdValue
    : DEFAULT_TRAINABILITY_THRESHOLD;

  const trainabilityScore = getTrainabilityScore10(player.trainability_index);
  const isTrainabilityAtRisk =
    Number.isFinite(trainabilityScore) && trainabilityScore < trainabilityThreshold;

  return hasPlayerActiveAlert(player, options) || isTrainabilityAtRisk;
}

export const PLAYER_AT_RISK_TRAINABILITY_THRESHOLD = DEFAULT_TRAINABILITY_THRESHOLD;

export const TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY = "teamOverview_riskMode";
export const TEAM_OVERVIEW_RISK_MODE_GLOBAL = "global";
export const TEAM_OVERVIEW_RISK_MODE_AT_RISK = "at-risk";
export const DEFAULT_TEAM_OVERVIEW_RISK_MODE = TEAM_OVERVIEW_RISK_MODE_AT_RISK;

export function readTeamOverviewRiskMode(storage) {
  if (!storage) return DEFAULT_TEAM_OVERVIEW_RISK_MODE;
  try {
    const saved = storage.getItem(TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY);
    if (saved === TEAM_OVERVIEW_RISK_MODE_GLOBAL) return TEAM_OVERVIEW_RISK_MODE_GLOBAL;
    if (saved === TEAM_OVERVIEW_RISK_MODE_AT_RISK) return TEAM_OVERVIEW_RISK_MODE_AT_RISK;
    return DEFAULT_TEAM_OVERVIEW_RISK_MODE;
  } catch {
    return DEFAULT_TEAM_OVERVIEW_RISK_MODE;
  }
}
